import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Field2 as Field,
  DaySelector,
  VibeModal,
  color,
} from 'vibeguide';
import Grid from '@mui/material/Grid';

// list of hours to select for start/end time
const hourOptions = [];

for (let i = 0; i <= 24; i++) {
  const hour = i < 10
    ? `0${i}:00`
    : `${i}:00`;

  hourOptions.push({
    label: hour,
    value: hour,
  });
}

function Information({
  className,
  name,
  mediaFormat,
  locationSpec,
  spotType,
  spotSelectionStrategy,
  allocateRemainder,
  startDate,
  endDate,
  startTime,
  endTime,
  daysOfWeek,
  disableInput,
  onUpdate,
  segmentType,
}) {
  const [confirmChangeMediaFormat, setConfirmChangeMediaFormat] = useState(false);
  const [newMediaFormat, setNewMediaFormat] = useState(null);

  /**
   * When the Media Format field is changed
   * ask to confirm removing all locations for the segment
   */
  const onChangeMediaFormat = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    if (locationSpec.length > 0) {
      // show confirmation modal that the locations will be reset
      setConfirmChangeMediaFormat(true);
      setNewMediaFormat(value);
    } else {
      // segment has no locations, allow changing without confirmation
      onUpdate({
        mediaFormat: value,
      });
    }
  };

  /**
   * Confirm changing the Media Format
   */
  const onConfirmChangeMediaFormat = () => {
    onUpdate({
      mediaFormat: newMediaFormat,
      // reset the locations when the media format is changed
      locations: {},
      locationSpec: [],
      locationsData: {
        companies: [],
        locations: [],
      },
      locationCount: 0,
    });

    setConfirmChangeMediaFormat(false);
    setNewMediaFormat(null);
  };

  /**
   * Cancel changing the Media Format
   */
  const onCancelChangeMediaFormat = () => {
    setConfirmChangeMediaFormat(false);
    setNewMediaFormat(null);
  };

  /**
   * When a field is changed
   */
  const onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    onUpdate({
      [name]: value,
    });
  };

  const onChangeAllocateRemainder = allocateRemainder => {
    onUpdate({
      allocateRemainder,
    });
  };

  const onChangeStartDate = (startDate) => {
    onUpdate({
      startDate,
    });
  };

  const onChangeEndDate = (endDate) => {
    onUpdate({
      endDate,
    });
  };

  const onChangeDays = (days) => {
    onUpdate({
      daysOfWeek: days,
    });
  };

  const onChangeSegmentType = (e) => {
    const {
      name,
      value,
    } = e.target;

    const data = {};

    if (value === 'added-value') {
      // remove the budget and cpm for added value flights
      data.budget = 0;
      data.cpm = 0;
      data.lockCalculator = false;
    }

    onUpdate({
      [name]: value,
      ...data,
    });
  };

  return (
    <div className={classNames('Information', 'segment', className)}>
      <Grid
        className="row"
        spacing={2}
        container
      >
        <Grid
          className="details-title"
          xs={6}
          item
        >
          Information
        </Grid>
      </Grid>

      <Grid
        className="row"
        spacing={2}
        container
      >
        <Grid
          xs={12}
          item
        >
          <Field
            type="text"
            label="Name"
            name="name"
            placeholder="Name"
            value={name}
            disabled={disableInput}
            tabIndex={20}
            onChange={onChange}
            autoFocus
            required
          />
        </Grid>

        <Grid
          xs={12}
          item
        >
          <Field
            type="select"
            label="Segment Type"
            name="segmentType"
            value={segmentType}
            options={[
              {
                label: 'Standard',
                value: 'standard',
              },
              {
                label: 'Added Value',
                value: 'added-value',
              },
            ]}
            disabled={disableInput}
            tabIndex={29}
            onChange={onChangeSegmentType}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Field
            type="select"
            label="Media Format"
            name="mediaFormat"
            value={mediaFormat}
            options={[
              {
                label: '',
                value: '',
              },
              {
                label: 'Audio',
                value: 'audio',
              },
              {
                label: 'Visual',
                value: 'visual',
              },
            ]}
            disabled={disableInput}
            tabIndex={21}
            onChange={onChangeMediaFormat}
            required
          />
        </Grid>

        <Grid
          xs={12}
          item
        >
          <Field
            type="select"
            label="Spot Type"
            name="spotType"
            value={spotType}
            options={[
              {
                label: '',
                value: '',
              },
              {
                label: 'General',
                value: 'general',
              },
              {
                label: 'Endemic',
                value: 'endemic',
              },
              {
                label: 'Non Endemic',
                value: 'non-endemic',
              },
              {
                label: 'Adult Beverage',
                value: 'adult-beverage',
              },
            ]}
            disabled={disableInput}
            tabIndex={22}
            onChange={onChange}
            required
          />
        </Grid>

        <Grid
          xs={12}
          item
        >
          <Field
            type="select"
            label="Spot Selection Strategy"
            name="spotSelectionStrategy"
            value={spotSelectionStrategy}
            options={[
              {
                label: '',
                value: '',
              },
              {
                label: 'Minimize Frequency',
                value: 'minimize-frequency',
              },
              {
                label: 'Maximize Frequency',
                value: 'maximize-frequency',
              },
            ]}
            disabled={disableInput}
            tabIndex={23}
            onChange={onChange}
            required
          />
        </Grid>

        <Grid
          xs={12}
          item
        >
          <Field
            type="toggle"
            label="Allocate Remainder"
            toggleProps={{
              label: 'Allocate Remainder',
            }}
            value={allocateRemainder ? 'on' : 'off'}
            tabIndex={24}
            disabled={disableInput}
            onChange={onChangeAllocateRemainder}
          />
        </Grid>

        <Grid
          xs={6}
          item
        >
          <Field
            type="datepicker"
            label="Start Date"
            placeholder="YYYY-MM-DD"
            value={startDate}
            dateProps={{
              format: 'YYYY-MM-DD',
            }}
            disabled={disableInput}
            tabIndex={25}
            onChange={onChangeStartDate}
            required
          />
        </Grid>

        <Grid
          xs={6}
          item
        >
          <Field
            type="datepicker"
            label="End Date"
            placeholder="YYYY-MM-DD"
            value={endDate}
            dateProps={{
              format: 'YYYY-MM-DD',
            }}
            disabled={disableInput}
            tabIndex={26}
            onChange={onChangeEndDate}
            required
          />
        </Grid>

        <Grid
          xs={6}
          item
        >
          <Field
            type="select"
            label="Start Time"
            name="startTime"
            value={startTime}
            options={hourOptions}
            disabled={disableInput}
            tabIndex={27}
            onChange={onChange}
            required
          />
        </Grid>

        <Grid
          xs={6}
          item
        >
          <Field
            type="select"
            label="End Time"
            name="endTime"
            value={endTime}
            options={hourOptions}
            disabled={disableInput}
            tabIndex={28}
            onChange={onChange}
            required
          />
        </Grid>

        <Grid
          xs={12}
          item
        >
          <Field
            type="custom"
            label="Days"
            required
          >
            <DaySelector
              style={{
                width: 250,
              }}
              dayFormat="dd"
              disabled={disableInput}
              selectedDays={daysOfWeek}
              toggleAll
              onChange={onChangeDays}
            />
          </Field>
        </Grid>
      </Grid>

      <VibeModal
        show={confirmChangeMediaFormat}
        type="confirm"
        title="Change Media Format"
        text={(
          <div>
            Changing the media format will reset the assigned locations for this segment
            <br />
            <br />
            This action cannot be undone.
          </div>
        )}
        confirmProps={{
          text: 'Confirm',
          color: color.aquaForest,
        }}
        cancelProps={{
          text: 'Cancel',
          color: color.manatee,
        }}
        onConfirm={onConfirmChangeMediaFormat}
        onClose={onCancelChangeMediaFormat}
      />
    </div>
  );
}

Information.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  mediaFormat: PropTypes.string,
  locationSpec: PropTypes.arrayOf(PropTypes.object),
  spotType: PropTypes.string,
  spotSelectionStrategy: PropTypes.string,
  allocateRemainder: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  daysOfWeek: PropTypes.arrayOf(PropTypes.string),
  disableInput: PropTypes.bool,
  onUpdate: PropTypes.func,
};

Information.defaultProps = {
  className: '',
  name: '',
  mediaFormat: '',
  locationSpec: [],
  spotType: '',
  spotSelectionStrategy: '',
  allocateRemainder: false,
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  daysOfWeek: [],
  disableInput: false,
  onUpdate: () => {},
};

export default Information;
